/**
 * columnStatus — shared status derivation for a target column.
 *
 * Both MapperSidebar and MapperInspector need to know "what is this column's
 * state right now?" — explicit rule, owned by a module, N/A-by-default, or
 * just unset. Centralising the logic here keeps the two surfaces aligned so
 * they never disagree (e.g. sidebar saying "module" while inspector still
 * renders an empty rule shell).
 *
 * Precedence:
 *   1. profile.fields[col] exists      → "mapped"  (user override wins)
 *   2. moduleForColumn returns module  → "module"
 *   3. naByDefault includes col        → "na"
 *   4. otherwise                       → "unset"
 */

import { moduleForColumn, type Module } from "@/lib/mapper/modules";
import type { Profile } from "@/lib/mapper/types";

export type ColumnStatus =
  | { kind: "mapped"; isAI: boolean }
  | { kind: "module"; module: Module }
  | { kind: "na" }
  | { kind: "unset" };

function isReadonlySet<T>(
  v: ReadonlySet<T> | readonly T[],
): v is ReadonlySet<T> {
  return v instanceof Set;
}

export function columnStatusFor(
  profile: Profile,
  column: string,
  naByDefault: ReadonlySet<string> | readonly string[],
): ColumnStatus {
  const rule = profile.fields[column];
  if (rule !== undefined) {
    return { kind: "mapped", isAI: rule.kind === "ai" };
  }
  const mod = moduleForColumn(profile, column);
  if (mod) return { kind: "module", module: mod };
  const isNA = isReadonlySet(naByDefault)
    ? naByDefault.has(column)
    : naByDefault.includes(column);
  if (isNA) return { kind: "na" };
  return { kind: "unset" };
}

/** Tailwind class tokens for each module tone. Kept here so both surfaces
 *  resolve identical colour treatments. */
export const TONE_CLASSES: Record<
  Module["tone"],
  {
    /** Solid square mark (for the sidebar status marker). */
    mark: string;
    /** Chip background + ring + text colors (sidebar + inspector). */
    chip: string;
    /** Border colour for the inspector module card top border. */
    border: string;
    /** Subtle tinted background for the inspector module card. */
    cardBg: string;
    /** Strong title text colour for the inspector module card. */
    title: string;
    /** Faded body text colour inside the inspector module card. */
    body: string;
  }
> = {
  teal: {
    mark: "bg-teal-500",
    chip: "bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:ring-teal-800",
    border: "border-teal-400 dark:border-teal-800",
    cardBg: "bg-teal-50/50 dark:bg-teal-950/30",
    title: "text-teal-900 dark:text-teal-200",
    body: "text-teal-800/85 dark:text-teal-300/85",
  },
  indigo: {
    mark: "bg-indigo-500",
    chip: "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:ring-indigo-800",
    border: "border-indigo-400 dark:border-indigo-800",
    cardBg: "bg-indigo-50/50 dark:bg-indigo-950/30",
    title: "text-indigo-900 dark:text-indigo-200",
    body: "text-indigo-800/85 dark:text-indigo-300/85",
  },
  amber: {
    mark: "bg-amber-500",
    chip: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800",
    border: "border-amber-400 dark:border-amber-800",
    cardBg: "bg-amber-50/50 dark:bg-amber-950/30",
    title: "text-amber-900 dark:text-amber-200",
    body: "text-amber-800/85 dark:text-amber-300/85",
  },
  violet: {
    mark: "bg-violet-500",
    chip: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800",
    border: "border-violet-400 dark:border-violet-800",
    cardBg: "bg-violet-50/50 dark:bg-violet-950/30",
    title: "text-violet-900 dark:text-violet-200",
    body: "text-violet-800/85 dark:text-violet-300/85",
  },
  sky: {
    mark: "bg-sky-500",
    chip: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800",
    border: "border-sky-400 dark:border-sky-800",
    cardBg: "bg-sky-50/50 dark:bg-sky-950/30",
    title: "text-sky-900 dark:text-sky-200",
    body: "text-sky-800/85 dark:text-sky-300/85",
  },
};
